import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useToast } from '../context/ToastContext';

const STATUS_LIST = ['all', 'pending', 'packed', 'dispatched', 'delivered', 'cancelled', 'awaiting_payment'];

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const toast = useToast();

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filter !== 'all') params.set('status', filter);
      const { data } = await api.get(`/orders?${params}`);
      setOrders(data.data.orders);
      setTotal(data.data.total);
      setTotalPages(data.data.totalPages || 1);
    } catch { toast('Failed to load orders', 'error'); }
    finally { setLoading(false); }
  }, [filter, page, toast]);

  useEffect(() => { fetch(); }, [fetch]);

  const updateStatus = async (orderId, status) => {
    try {
      await api.put(`/orders/${orderId}/status`, { status });
      toast('Status updated!', 'success');
      fetch();
    } catch { toast('Failed to update', 'error'); }
  };

  const viewInvoice = (orderId) => {
    window.open(`${api.defaults.baseURL}/orders/${orderId}/invoice`, '_blank');
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700 }}>📦 Orders</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>{total} total orders</p>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {STATUS_LIST.map(s => (
          <button
            key={s}
            className={`btn btn-sm ${filter === s ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => { setFilter(s); setPage(1); }}
            style={{ textTransform: 'capitalize' }}
          >
            {s === 'all' ? '🗂 All' : s}
          </button>
        ))}
      </div>

      <div className="card">
        {loading ? (
          <div className="flex-center" style={{ height: 200 }}><div className="spinner"></div></div>
        ) : orders.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">📭</div><p>No orders found</p></div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Apartment</th>
                  <th>Amount</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id}>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>
                        #{o.id.slice(0, 8).toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{o.customer_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{o.phone}</div>
                    </td>
                    <td style={{ fontSize: 13 }}>{o.apartment_name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td style={{ fontWeight: 700 }}>₹{parseFloat(o.total_amount).toLocaleString('en-IN')}</td>
                    <td>
                      <span style={{ fontSize: 11, textTransform: 'uppercase', color: o.payment_method === 'cod' ? 'var(--warning)' : 'var(--success)', fontWeight: 600 }}>
                        {o.payment_method}
                      </span>
                    </td>
                    <td>
                      <select
                        value={o.order_status}
                        onChange={e => updateStatus(o.id, e.target.value)}
                        style={{ background: 'var(--dark-3)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '4px 8px', fontSize: 12, cursor: 'pointer' }}
                      >
                        {['pending','packed','dispatched','delivered','cancelled','awaiting_payment'].map(s => (
                          <option key={s} value={s}>{s === 'awaiting_payment' ? '⏳ awaiting payment' : s}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {new Date(o.created_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'2-digit' })}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/orders/${o.id}`)}>View</button>
                        {o.invoice_url && (
                          <button className="btn btn-primary btn-sm" onClick={() => viewInvoice(o.id)} style={{ padding: '4px 8px' }}>
                            📄 PDF
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <button className="btn btn-secondary btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span style={{ padding: '6px 14px', fontSize: 13 }}>Page {page} of {totalPages}</span>
            <button className="btn btn-secondary btn-sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
